import React, { useState, useRef, useMemo, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import PlayerNameButton from '../../components/playerSheet/PlayerNameButton';
import { AgGridReact } from 'ag-grid-react';
import {
  ColDef,
  AllCommunityModule,
  ModuleRegistry,
  ICellRendererParams,
  IGetRowsParams,
  RowStyle,
} from 'ag-grid-community';
import AddPlayerModal from '../../components/modals/AddPlayerModal';
import ConfirmDialog from '../../components/modals/ConfirmDialog';
import { WelcomeModal } from '../../components/modals';
import { RosterUploadModal } from '../../components/upload';
import api from '../../config/axiosConfig';
import { API_URLS } from '../../config/Constants';
import { showErrorMsg, showSuccessMsg } from '../../config/common';
import { sessionManager } from '../../config/sessionManager';
import { PermissionGuard } from '../../components/common/PermissionGuard';
import 'react-toastify/dist/ReactToastify.css';

// Register AG Grid modules
ModuleRegistry.registerModules([AllCommunityModule]);

interface PlayerData {
  _id?: string;
  firstName: string;
  lastName: string;
  number: number | null;
  graduation: string | null;
  position: string | null;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
  clientId?: string;
  clientName?: string;
  active?: boolean;
  [key: string]: any;
}

const Players: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [playerToDelete, setPlayerToDelete] = useState<PlayerData | null>(null);
  const [editPlayer, setEditPlayer] = useState<PlayerData | null>(null);
  const [pageSize, setPageSize] = useState(100);
  const [isLoading, setIsLoading] = useState(true);
  const [includeArchived, setIncludeArchived] = useState(false);
  const [showWelcomeModal, setShowWelcomeModal] = useState(false);
  const [clientName, setClientName] = useState<string>('');
  const [welcomeTargetRoute, setWelcomeTargetRoute] = useState<string>('');
  const navigate = useNavigate();
  const clientIdFromSession = sessionManager.getClientId();
  const gridApi = useRef<any>(null);

  // Check if user was redirected here after invitation acceptance (URL param)
  // OR if this is a new self-signup user (sessionStorage flag from login)
  const isNewUserFromUrl = searchParams.get('welcome') === 'true';

  // Check for new self-signup user and show welcome modal
  useEffect(() => {
    const clientId = sessionManager.getClientId();
    if (!clientId) return;

    // Check sessionStorage for new self-signup user flag
    const showWelcomeFromLogin = sessionStorage.getItem('showWelcomeModal') === 'true';
    const welcomeClientNameFromLogin = sessionStorage.getItem('welcomeClientName') || '';
    const welcomeTargetFromLogin = sessionStorage.getItem('welcomeTargetRoute') || '';

    // If new self-signup user, show modal immediately with stored client name
    if (showWelcomeFromLogin) {
      setClientName(welcomeClientNameFromLogin);
      setWelcomeTargetRoute(welcomeTargetFromLogin);
      setShowWelcomeModal(true);
      // Clear the flag so it doesn't show again on refresh
      sessionStorage.removeItem('showWelcomeModal');
      sessionStorage.removeItem('welcomeClientName');
      sessionStorage.removeItem('welcomeTargetRoute');
      return;
    }

    // For URL-based welcome (invite acceptance), fetch client info
    if (isNewUserFromUrl) {
      const fetchClientInfo = async () => {
        try {
          const response = await api.get(API_URLS.GET_CLIENT_BY_ID, {
            params: { clientId: clientId },
          });
          if (response.data.success) {
            const data = response.data.data;
            setClientName(data.name || '');
            setShowWelcomeModal(true);
          }
        } catch (error) {
          console.error('Error fetching client info:', error);
          // Still show welcome modal even if client fetch fails
          setShowWelcomeModal(true);
        }
      };
      fetchClientInfo();
    }
  }, [isNewUserFromUrl]);

  // Define the datasource outside of onGridReady
  const datasource = useMemo(
    () => ({
      getRows: async (getRowsParams: IGetRowsParams) => {
        const page = Math.floor(getRowsParams.startRow / pageSize);
        const limit = getRowsParams.endRow - getRowsParams.startRow;
        if (getRowsParams.startRow === 0) {
          setIsLoading(true);
        }

        try {
          const params: any = {
            page: page + 1,
            limit: limit,
          };
          // Scope to current client when available
          if (clientIdFromSession) {
            params.clientId = clientIdFromSession;
          }
          // Include archived players if toggle is enabled
          if (includeArchived) {
            params.includeArchived = 'true';
          }

          // Add sorting parameters if sort model exists
          const sortModel = getRowsParams.sortModel;
          if (sortModel && sortModel.length > 0) {
            const sort = sortModel[0];
            // Map AG Grid column IDs to backend field names
            const fieldMapping: { [key: string]: string } = {
              no: 'no',
              // 'name' field is a computed field, map to 'firstName'
              name: 'name',
              position: 'position',
              graduation: 'graduation',
              active: 'active',
            };
            params.sortField = fieldMapping[sort.colId] || sort.colId;
            params.sortOrder = sort.sort; // 'asc' or 'desc'
          }

          const response = await api.get(API_URLS.GET_PLAYERS, { params });
          if (response.data.success) {
            getRowsParams.successCallback(response.data.data, response.data.total);
            if (getRowsParams.startRow === 0) setIsLoading(false);
          } else {
            showErrorMsg(response.data.message || 'Failed to fetch players');
            getRowsParams.failCallback();
            setIsLoading(false);
          }
        } catch (error) {
          showErrorMsg('Failed to fetch players.');
          getRowsParams.failCallback();
          setIsLoading(false);
        }
      },
    }),
    [pageSize, clientIdFromSession, includeArchived],
  );

  // On grid ready, just save the api ref
  const onGridReady = (params: any) => {
    gridApi.current = params.api;
    setIsLoading(true);
  };

  // Handle pagination changes (including page size changes)
  const onPaginationChanged = (params?: any) => {
    if (!gridApi.current) return;
    const newPageSize = gridApi.current.paginationGetPageSize();
    if (newPageSize !== pageSize) {
      setPageSize(newPageSize);
      gridApi.current.purgeInfiniteCache();
    }
  };

  // Handle sort changes
  const onSortChanged = () => {
    if (!gridApi.current) return;
    // Purge cache to trigger new fetch with sort parameters
    gridApi.current.purgeInfiniteCache();
  };

  // Handle toggle for showing archived players
  const handleArchiveToggle = () => {
    setIncludeArchived(!includeArchived);
    // Refresh the grid to apply the filter change
    if (gridApi.current) {
      gridApi.current.refreshInfiniteCache();
    }
  };

  // Edit button logic
  const handleEditClick = (player: PlayerData) => {
    setEditPlayer(player);
    setIsModalOpen(true);
  };

  // Delete button logic
  const handleDeleteClick = (player: PlayerData) => {
    setPlayerToDelete(player);
    setIsDeleteDialogOpen(true);
  };

  // Confirm delete
  const handleDeleteConfirm = async () => {
    if (playerToDelete && playerToDelete._id) {
      try {
        const response = await api.delete(API_URLS.DELETE_PLAYER, {
          params: { _id: playerToDelete._id },
        });
        if (response.data.success) {
          showSuccessMsg('Player removed successfully!');
          if (gridApi.current) {
            gridApi.current.purgeInfiniteCache();
          }
        } else {
          showErrorMsg(response.data.message || 'Failed to remove player');
        }
      } catch (error) {
        showErrorMsg('Failed to remove player.');
      }
    }
    setIsDeleteDialogOpen(false);
    setPlayerToDelete(null);
  };

  // Only refresh grid after add or update
  const handlePlayerAddedOrUpdated = () => {
    setIsModalOpen(false);
    setEditPlayer(null);
    if (gridApi.current) {
      gridApi.current.purgeInfiniteCache();
    }
  };

  // Only close modal, do not refresh grid
  const handleModalClose = () => {
    setIsModalOpen(false);
    setEditPlayer(null);
  };

  // Custom cell renderer for status column
  const StatusCellRenderer: React.FC<ICellRendererParams<PlayerData>> = ({ value }) => {
    const isActive = value === true || value === 'Active';
    return (
      <span
        className={`px-2 py-1 rounded-full ${isActive ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}
      >
        {isActive ? 'Active' : 'Archived'}
      </span>
    );
  };

  // Custom cell renderer for actions column
  const ActionsCellRenderer: React.FC<ICellRendererParams<PlayerData>> = ({ data }) => {
    if (!data) return null;

    const canEdit = sessionManager.canPerformAction('players', 'edit');
    const canDelete = sessionManager.canPerformAction('players', 'delete');

    return (
      <div className="flex items-center justify-center space-x-2">
        {canEdit && (
          <button
            onClick={() => handleEditClick(data)}
            className="text-blue-600 hover:text-blue-800 transform hover:scale-110 transition-all"
            title="Edit Player"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              className="w-4 h-4"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.536L16.732 3.732z"
              />
            </svg>
          </button>
        )}
        {canDelete && (
          <button
            onClick={() => handleDeleteClick(data)}
            className="text-red-600 hover:text-red-800 transform hover:scale-110 transition-all"
            title="Delete Player"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              className="w-4 h-4"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
              />
            </svg>
          </button>
        )}
      </div>
    );
  };

  const columnDefs: ColDef<PlayerData>[] = [
    {
      headerName: '#',
      field: 'no',
      colId: 'no',
      sortable: true,
      flex: 0.6,
      minWidth: 70,
      valueGetter: (p) => p.data?.no ?? '',
      cellClass: 'font-semibold',
    },
    {
      headerName: 'Name',
      colId: 'name',
      sortable: true,
      flex: 2,
      minWidth: 150,
      valueGetter: (params) => {
        const firstName = params.data?.firstName || '';
        const lastName = params.data?.lastName || '';
        return `${firstName} ${lastName}`.trim();
      },
      cellRenderer: (params: ICellRendererParams<PlayerData>) => {
        const name = `${params.data?.firstName || ''} ${params.data?.lastName || ''}`.trim() || '-';
        if (!params.data?._id) return name;
        return (
          <PlayerNameButton playerId={params.data._id} seasonId={sessionManager.getSeasonId()} variant="career">
            {name}
          </PlayerNameButton>
        );
      },
    },
    {
      headerName: 'Position',
      sortable: true,
      flex: 2,
      minWidth: 150,
      field: 'position',
      colId: 'position',
      valueGetter: (params) => {
        const position = params.data?.position || '';
        if (Array.isArray(position)) {
          return position.join(', ');
        }
        return position;
      },
    },
    {
      headerName: 'Year',
      sortable: true,
      flex: 1,
      minWidth: 80,
      field: 'graduation',
      colId: 'graduation',
      valueGetter: (params) => {
        return params.data?.graduation || '';
      },
    },
    {
      field: 'active',
      colId: 'active',
      headerName: 'Status',
      sortable: true,
      flex: 1,
      cellRenderer: StatusCellRenderer,
      minWidth: 100,
    },
    {
      headerName: 'Actions',
      cellRenderer: ActionsCellRenderer,
      sortable: false,
      width: 100,
      cellStyle: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minWidth: 100,
      },
    },
  ];

  // Function to apply alternating row styles
  const getRowStyle = (params: any): RowStyle => {
    const isArchived = params.data?.active === false;

    if (isArchived) {
      // Archived players get a subtle gray background
      return {
        backgroundColor: params.node.rowIndex % 2 === 1 ? '#f5f5f5' : '#f9f9f9',
        opacity: 0.7,
      };
    }

    if (params.node.rowIndex % 2 === 1) {
      return { backgroundColor: 'rgb(var(--sd-mint))' };
    }
    return {};
  };

  return (
    <div className="w-full p-4">
      {/* Header Section */}
      <div className="mb-6">
        {/* Title Row */}
        <div className="flex justify-between items-center mb-4">
          <h1 className="text-2xl font-bold text-gray-900">Players</h1>
        </div>

        {/* Filter Row */}
        <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-center py-3 px-4 bg-gray-50 rounded-lg border">
          <label className="flex items-center cursor-pointer">
            <input type="checkbox" checked={includeArchived} onChange={handleArchiveToggle} className="sr-only" />
            <div
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200 ${
                includeArchived ? 'bg-blue-600' : 'bg-gray-300'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform duration-200 ${
                  includeArchived ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </div>
            <span className="ml-3 text-sm font-medium text-gray-700">Show Archived Players</span>
          </label>
          <div className="flex items-center space-x-3">
            <PermissionGuard resource="players" action="add">
              <button
                onClick={() => setIsUploadModalOpen(true)}
                className="inline-flex w-full text-nowrap sm:w-auto items-center px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors duration-200 shadow-sm"
              >
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                  />
                </svg>
                Upload Roster
              </button>
            </PermissionGuard>
            <PermissionGuard resource="players" action="add">
              <button
                onClick={() => {
                  setEditPlayer(null);
                  setIsModalOpen(true);
                }}
                className="inline-flex w-full sm:w-auto items-center px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 transition-colors duration-200 shadow-sm"
              >
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Add Player
              </button>
            </PermissionGuard>
          </div>
        </div>
      </div>

      <div className="ag-theme-alpine" style={{ height: 500, width: '100%' }}>
        <AgGridReact
          columnDefs={columnDefs}
          rowModelType="infinite"
          pagination={true}
          paginationPageSize={pageSize}
          paginationPageSizeSelector={[25, 50, 100]}
          cacheBlockSize={pageSize}
          datasource={datasource}
          onGridReady={onGridReady}
          onPaginationChanged={onPaginationChanged}
          onSortChanged={onSortChanged}
          loading={isLoading}
          blockLoadDebounceMillis={50}
          maxBlocksInCache={2}
          infiniteInitialRowCount={pageSize}
          overlayLoadingTemplate={
            '<div role="status"><svg aria-hidden="true" class="w-8 h-8 text-gray-200 animate-spin dark:text-gray-600 fill-blue-600" viewBox="0 0 100 101" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z" fill="currentColor"/><path d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z" fill="currentFill"/></svg><span class="sr-only">Loading...</span></div>'
          }
          overlayNoRowsTemplate={'<span class="ag-overlay-no-rows-center">No players found</span>'}
          getRowStyle={getRowStyle}
        />
      </div>

      {isModalOpen && (
        <AddPlayerModal onClose={handleModalClose} onPlayerAdded={handlePlayerAddedOrUpdated} editPlayer={editPlayer} />
      )}
      {isDeleteDialogOpen && (
        <ConfirmDialog
          isOpen={isDeleteDialogOpen}
          onClose={() => setIsDeleteDialogOpen(false)}
          onConfirm={handleDeleteConfirm}
          title="Delete Player"
          message={`Are you sure you want to delete ${playerToDelete?.firstName} ${playerToDelete?.lastName}? This action cannot be undone.`}
          confirmText="Delete"
          cancelText="Cancel"
          confirmButtonClass="bg-red-600 hover:bg-red-700"
        />
      )}
      {isUploadModalOpen && (
        <RosterUploadModal
          isOpen={isUploadModalOpen}
          onClose={() => setIsUploadModalOpen(false)}
          onUploadComplete={() => {
            // Refresh the grid data
            if (gridApi.current) {
              gridApi.current.refreshInfiniteCache();
            }
          }}
        />
      )}

      {/* Welcome Modal */}
      <WelcomeModal
        isOpen={showWelcomeModal}
        onClose={() => setShowWelcomeModal(false)}
        onGetStarted={() => {
          setShowWelcomeModal(false);
          if (welcomeTargetRoute) {
            navigate(welcomeTargetRoute);
          }
        }}
        clientName={clientName}
      />
    </div>
  );
};

export default Players;
